import {
  EventType,
  IWindowEventObserver,
  IWindowEventService
} from '../../services/window-event-service.ts';
import { globalAppContext } from '../app-context/global-app-context.tsx';
import { UBTracking } from './ubtracking.ts';
declare const window: Window; // & IUBTrackerWindow;

/**
 * Sends a 'time-on-site' tracking event, as the page gets hidden, with the duration for that the page was visible.
 */
export class TimeOnSite implements IWindowEventObserver {
  private startedAt: Date | undefined;
  private page: string | undefined;

  constructor(private windowEventService: IWindowEventService) {
    /**
     * The following quote describes well, why we use the page visibility API, instead of other events:
     *
     * > The transition to hidden is also often the last state change that's reliably observable by developers
     * > (this is especially true on mobile, as users can close tabs or the browser app itself,
     * > and the beforeunload, pagehide, and unload events are not fired in those cases).
     *
     * Quote source: https://developer.chrome.com/blog/page-lifecycle-api/#developer-recommendations-for-each-state
     */
    this.windowEventService.register(EventType.VisibilityChange, this);
  }

  start = (): void => {
    this.startedAt = new Date();
    this.page = window.location.href;
  };

  reset = (): void => {
    this.startedAt = undefined;
    this.page = undefined;
  };

  /**
   * Used to shut down between tests.
   */
  stop = (): void => {
    this.windowEventService.unregister(EventType.VisibilityChange, this);
    this.reset();
  };

  /**
   * returns the time on site value in seconds.
   *
   * @returns {number} seconds since the measurement started
   */
  private measure = (): number => {
    if (!this.startedAt) {
      globalAppContext()?.logger.error("Cannot measure time on site since timer wasn't started");
      return 0;
    }

    return Math.abs(new Date().getTime() - this.startedAt.getTime()) / 1000;
  };

  /**
   * stops the timer and sends a tracking event via beacon.
   */
  measureAndSend = (): void => {
    const timeOnSite = this.measure();

    if (timeOnSite !== undefined && this.page) {
      const value = String(timeOnSite);

      try {
        UBTracking.trackEvent.timeOnSite(value);
        this.reset();
      } catch (e) {
        globalAppContext()?.logger.error('TimeOnSite :: Failed to send time on site', e);
      }
    } else {
      // This case happens when the user leaves the page before the start() could be called.
      globalAppContext()?.logger.error(
        `TimeOnSite not initialized correctly – ${timeOnSite} – ${this.page}`
      );
    }
  };

  /**
   * visibilityChange event listeners (user leaves tab or comes back)
   */
  onPageVisible = (): void => {
    this.start();
  };

  onPageHidden = (): void => {
    this.measureAndSend();
  };
}
